import { SearchResult, RequestStatus } from '../types'
import Pagination from './Pagination'
import LoadingSpinner from './LoadingSpinner'
import './SearchResults.css'

export interface SearchResultsProps {
  results: SearchResult[]
  loading?: boolean
  onViewDocument?: (result: SearchResult) => void
  currentPage: number
  pageSize: number
  total: number
  onPageChange?: (page: number) => void
}

// Format date for display
const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  } catch {
    return dateString
  }
}

// Get status badge class based on status
const getStatusClass = (status: RequestStatus): string => {
  const statusMap: Record<RequestStatus, string> = {
    'Pending': 'search-results__badge--pending',
    'Processing': 'search-results__badge--processing',
    'Completed': 'search-results__badge--completed',
    'Rejected': 'search-results__badge--rejected'
  }
  return statusMap[status] || ''
}

function SearchResults({
  results,
  loading = false,
  onViewDocument,
  currentPage,
  pageSize,
  total,
  onPageChange
}: SearchResultsProps) {
  const totalPages = Math.ceil(total / pageSize)

  // Loading state
  if (loading) {
    return (
      <div className="search-results">
        <div className="search-results__loading">
          <LoadingSpinner size="large" label="Searching..." />
        </div>
      </div>
    )
  }

  // Empty state - show empty paginated list as per Requirement 3.7
  if (results.length === 0) {
    return (
      <div className="search-results">
        <div className="search-results__empty">
          <div className="search-results__empty-icon" aria-hidden="true">
            <svg
              width="64"
              height="64"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
              <path d="M8 8l6 6" />
              <path d="M14 8l-6 6" />
            </svg>
          </div>
          <h3 className="search-results__empty-title">No Results Found</h3>
          <p className="search-results__empty-message">
            No certificates of analysis match your search criteria. Try adjusting your search terms or date range.
          </p>
        </div>
        {totalPages > 1 && onPageChange && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
          />
        )}
      </div>
    )
  }

  // Handle page change
  const handlePageChange = (page: number) => {
    if (onPageChange) {
      onPageChange(page)
    }
  }

  return (
    <div className="search-results">
      {/* Results info */}
      <div className="search-results__info" role="status" aria-live="polite">
        Showing {((currentPage - 1) * pageSize) + 1} to{' '}
        {Math.min(currentPage * pageSize, total)} of {total} results
      </div>

      {/* Results table */}
      <div className="search-results__table-container">
        <table className="search-results__table" aria-label="Search results">
          <thead>
            <tr>
              <th scope="col">Reference #</th>
              <th scope="col">Company Name</th>
              <th scope="col">Analysis Type</th>
              <th scope="col">Submitted Date</th>
              <th scope="col">Status</th>
              {onViewDocument && <th scope="col"><span className="visually-hidden">Actions</span></th>}
            </tr>
          </thead>
          <tbody>
            {results.map((result) => (
              <tr key={result.id} className="search-results__row">
                <td className="search-results__cell search-results__cell--product">
                  <span className="search-results__product-name">
                    {result.productName || result.id}
                  </span>
                </td>
                <td className="search-results__cell">
                  <span className="search-results__lot-number">
                    {result.lotNumber || '—'}
                  </span>
                </td>
                <td className="search-results__cell">
                  <span className="search-results__analysis-type">
                    {result.analysisType || '—'}
                  </span>
                </td>
                <td className="search-results__cell">
                  <time className="search-results__date" dateTime={result.submittedAt}>
                    {formatDate(result.submittedAt)}
                  </time>
                </td>
                <td className="search-results__cell">
                  <span className={`search-results__badge ${getStatusClass(result.status)}`}>
                    {result.status}
                  </span>
                </td>
                {onViewDocument && (
                  <td className="search-results__cell search-results__cell--actions">
                    <button
                      className="search-results__action-button"
                      onClick={() => onViewDocument(result)}
                      aria-label={`View document for ${result.productName}`}
                    >
                      View
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && onPageChange && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  )
}

export default SearchResults